import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../utils/api';
import { Button } from '../components/ui/button';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | failed | expired | timeout | error
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await authFetch(`/api/payments/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error('status fetch failed');
        const data = await res.json();
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'expired') {
          setState('expired');
          return;
        }
        if (data.payment_status === 'failed') {
          setState('failed');
          return;
        }
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setState('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setState('error');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const T = {
    el: {
      checking: 'Επιβεβαίωση πληρωμής...',
      paid_title: 'Η πληρωμή ολοκληρώθηκε!',
      paid_desc: 'Ευχαριστούμε για την εγγραφή σας. Θα λάβετε email επιβεβαίωσης σύντομα.',
      failed_title: 'Η πληρωμή απέτυχε',
      failed_desc: 'Δοκιμάστε ξανά ή επικοινωνήστε μαζί μας.',
      expired_title: 'Η συνεδρία πληρωμής έληξε',
      expired_desc: 'Παρακαλώ ξεκινήστε ξανά τη διαδικασία.',
      timeout_title: 'Έλεγχος πληρωμής σε εκκρεμότητα',
      timeout_desc: 'Η επιβεβαίωση καθυστερεί. Θα λάβετε email μόλις ολοκληρωθεί.',
      error_title: 'Σφάλμα',
      error_desc: 'Λείπει το session ID ή προέκυψε πρόβλημα.',
      back: 'Επιστροφή στην αρχική',
      tryAgain: 'Δοκιμάστε ξανά',
    },
    en: {
      checking: 'Verifying your payment...',
      paid_title: 'Payment successful!',
      paid_desc: 'Thank you for subscribing. You will receive a confirmation email shortly.',
      failed_title: 'Payment failed',
      failed_desc: 'Please try again or contact us.',
      expired_title: 'Checkout session expired',
      expired_desc: 'Please start the checkout again.',
      timeout_title: 'Verification pending',
      timeout_desc: 'Confirmation is delayed. You will receive an email once completed.',
      error_title: 'Error',
      error_desc: 'Missing session ID or an error occurred.',
      back: 'Back to home',
      tryAgain: 'Try again',
    },
  };
  const t = T[language] || T.en;

  return (
    <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 text-center">
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-cyan-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-status-checking">{t.checking}</h1>
          </>
        )}

        {state === 'paid' && (
          <>
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-status-paid">{t.paid_title}</h1>
            <p className="text-white/70 mb-6">{t.paid_desc}</p>
            {details && (
              <p className="text-white/50 text-sm mb-6">
                {details.amount_total != null && (
                  <>€{(details.amount_total / 100).toFixed(2)} {String(details.currency || '').toUpperCase()}</>
                )}
              </p>
            )}
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white w-full"
              data-testid="payment-back-home"
            >
              {t.back}
            </Button>
          </>
        )}

        {(state === 'failed' || state === 'expired' || state === 'timeout' || state === 'error') && (
          <>
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2" data-testid={`payment-status-${state}`}>
              {t[`${state}_title`]}
            </h1>
            <p className="text-white/70 mb-6">{t[`${state}_desc`]}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/#pricing')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white w-full"
                data-testid="payment-try-again"
              >
                {t.tryAgain}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
                data-testid="payment-back-home"
              >
                {t.back}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
